import React, { createContext, useState, useContext, useEffect, useMemo, useCallback, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    createRagFolder,
    getRagFolders,
    updateRagFolder,
    deleteRagFolder,
    reorderRagFolders,
} from "../../api/ragFolders";
import { getCompanyFiles } from "../../api/companyFileCategories";
import { CompanyFileListItem } from "../../types/requestsOutputs";
import { deleteVectorDocument } from "../../api/vectorStores";
import { useNotification } from "../../context/NotificationContext";
import { MAX_TOTAL_FILES } from "./constants";

interface Folder {
    id: string;
    name: string;
}

interface DatabaseContextType {
    folders: Folder[];
    selectedFolderId: string;
    files: CompanyFileListItem[];
    isRefreshing: boolean;
    isDeleting: boolean;
    pagination: {
        currentPage: number;
        totalPages: number;
        totalCount: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        limit: number;
    };
    setFolders: React.Dispatch<React.SetStateAction<Folder[]>>;
    setFiles: React.Dispatch<React.SetStateAction<CompanyFileListItem[]>>;
    setSelectedFolderId: (id: string) => void;
    handleEditFolder: (id: string, newName: string) => Promise<void>;
    handleDeleteFolder: (id: string) => Promise<void>;
    handleSelectFolder: (id: string) => void;
    addFolder: (name: string) => Promise<void>;
    reorderFolders: (orderedFolderIds: string[]) => Promise<void>;
    refreshFolders: () => Promise<void>;
    refreshFiles: () => Promise<void>;
    handleDeleteFile: (fileId: string) => Promise<void>;
    hasActiveVectorSearch: boolean;
    isFileLimitReached: boolean;
    triggerQueueRefresh: () => void;
    queueRefreshTrigger: number;
    // New query param methods
    updateQueryParams: (params: Record<string, string | undefined>) => void;
    searchParams: URLSearchParams;
}

const DatabaseContext = createContext<DatabaseContextType | undefined>(undefined);

export const DatabaseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();
    const [searchParams, setSearchParams] = useSearchParams();
    const [folders, setFolders] = useState<Folder[]>([]);
    const [files, setFiles] = useState<CompanyFileListItem[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<string>("");
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [isFileLimitReached, setIsFileLimitReached] = useState<boolean>(false);
    const [queueRefreshTrigger, setQueueRefreshTrigger] = useState<number>(0);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        limit: 10,
    });
    const token = localStorage.getItem("token");

    // Update URL query params
    const updateQueryParams = useCallback(
        (params: Record<string, string | undefined>) => {
            const newParams = new URLSearchParams(searchParams);

            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === "" || value === null) {
                    newParams.delete(key);
                } else {
                    newParams.set(key, value);
                }
            });

            setSearchParams(newParams);
        },
        [searchParams, setSearchParams],
    );

    // Check if there's an active vector search
    const hasActiveVectorSearch = useMemo(() => {
        return !!searchParams.get("vectorQuery");
    }, [searchParams]);

    const refreshFiles = useCallback(async () => {
        if (!token) return;
        setIsRefreshing(true);
        try {
            // Build query params directly from current searchParams to avoid stale closures
            const params: any = {};

            // Pagination
            const page = searchParams.get("page");
            const limit = searchParams.get("limit");
            if (page) params.page = parseInt(page);
            if (limit) params.limit = parseInt(limit);

            // Sort
            const sortBy = searchParams.get("sortBy");
            const sortOrder = searchParams.get("sortOrder");
            if (sortBy) params.sortBy = sortBy;
            if (sortOrder) params.sortOrder = sortOrder;

            // Vector search
            const vectorQuery = searchParams.get("vectorQuery");
            const vectorDistance = searchParams.get("vectorDistance");
            const penaltyAlpha = searchParams.get("penaltyAlpha");
            const objectLimit = searchParams.get("objectLimit");
            if (vectorQuery) params.vectorQuery = vectorQuery;
            if (vectorDistance) params.vectorDistance = parseFloat(vectorDistance);
            if (penaltyAlpha) params.penaltyAlpha = parseFloat(penaltyAlpha);
            if (objectLimit) params.objectLimit = parseInt(objectLimit);

            // Filters
            const filterKeys = [
                "name",
                "url",
                "title",
                "description",
                "author",
                "companyRagFolderId",
                "type",
                "language",
                "categoryId",
                "tagId",
                "rightsOwner",
                "creditLine",
                "usageTypeId",
                "rightsTermId",
                "territoryId",
                "allowedMediaId",
                "createdAtFrom",
                "createdAtTo",
                "updatedAtFrom",
                "updatedAtTo",
                "dateFrom",
                "dateTo",
            ];
            filterKeys.forEach((key) => {
                const value = searchParams.get(key);
                if (value) params[key] = value;
            });

            const response = await getCompanyFiles({ token, queryParams: params });

            setFiles(response.data || []);
            setPagination(response.pagination);
            // Check if file limit is reached based on total count
            setIsFileLimitReached((response.pagination?.totalCount || 0) >= MAX_TOTAL_FILES);
        } catch (error) {
            console.error("Failed to fetch files:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [token, searchParams]);

    const refreshFolders = useCallback(async () => {
        if (!token) {
            console.error("No token available");
            return;
        }
        setIsRefreshing(true);
        try {
            const fetchedFolders = await getRagFolders({ token });
            setFolders(fetchedFolders);
        } catch (error) {
            console.error("Failed to fetch folders:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [token]);

    // Fetch folders once on mount
    useEffect(() => {
        if (token) {
            refreshFolders();
        }
    }, [token, refreshFolders]);

    // Fetch files whenever search params change
    useEffect(() => {
        if (token) {
            refreshFiles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, searchParams]); // refreshFiles is stable and reads current searchParams

    // Sync selectedFolderId with URL
    useEffect(() => {
        const folderId = searchParams.get("companyRagFolderId");
        setSelectedFolderId(folderId || "");
    }, [searchParams]);

    const handleEditFolder = useCallback(
        async (id: string, newName: string) => {
            if (!token) {
                console.error("No token available");
                return;
            }
            try {
                await updateRagFolder({ token, folderId: id, newName });
                setFolders((prevFolders) =>
                    prevFolders.map((folder) => (folder.id === id ? { ...folder, name: newName } : folder)),
                );
            } catch (error) {
                console.error("Failed to edit folder:", error);
            }
        },
        [token],
    );

    const handleDeleteFolder = useCallback(
        async (id: string) => {
            setIsDeleting(true);
            if (!token) {
                console.error("No token available");
                return;
            }
            setIsRefreshing(true);
            try {
                await deleteRagFolder({ token, folderId: id });
                setFolders((prevFolders) => prevFolders.filter((folder) => folder.id !== id));
                if (id === selectedFolderId) {
                    setSelectedFolderId("");
                }
            } catch (error) {
                console.error("Failed to delete folder:", error);
            } finally {
                setIsRefreshing(false);
                setIsDeleting(false);
            }
        },
        [token, selectedFolderId],
    );

    const handleSelectFolder = useCallback(
        (id: string) => {
            if (selectedFolderId === id) {
                // Deselect folder
                updateQueryParams({ companyRagFolderId: undefined });
            } else {
                // Select folder and reset to page 1
                updateQueryParams({ companyRagFolderId: id, page: "1" });
            }
        },
        [selectedFolderId, updateQueryParams],
    );

    const addFolder = useCallback(
        async (name: string) => {
            if (!token) {
                console.error("No token available");
                return;
            }
            try {
                const response = await createRagFolder({ token, name });
                const newFolder = { id: response.id, name };
                // Insert at the top to mirror the backend (which now creates folders at position 0).
                setFolders((prevFolders) => [newFolder, ...prevFolders]);
                setSelectedFolderId(newFolder.id);
            } catch (error) {
                console.error("Failed to add folder:", error);
            }
        },
        [token],
    );

    const reorderFolders = useCallback(
        async (orderedFolderIds: string[]) => {
            if (!token) {
                console.error("No token available");
                return;
            }
            // Optimistic update: keep a snapshot to be able to roll back on error.
            let snapshot: Folder[] = [];
            setFolders((prevFolders) => {
                snapshot = prevFolders;
                const byId = new Map(prevFolders.map((folder) => [folder.id, folder]));
                const reordered = orderedFolderIds
                    .map((id) => byId.get(id))
                    .filter((folder): folder is Folder => Boolean(folder));
                // Append any folder that was not part of the reorder request, keeping its prior order.
                const reorderedIds = new Set(orderedFolderIds);
                const remaining = prevFolders.filter((folder) => !reorderedIds.has(folder.id));
                return [...reordered, ...remaining];
            });

            try {
                await reorderRagFolders({ token, orderedFolderIds });
            } catch (error) {
                console.error("Failed to reorder folders:", error);
                setFolders(snapshot);
                showNotification({
                    message: t("reorderFoldersFailed") || "Failed to reorder folders, please try again.",
                    type: "warning",
                });
            }
        },
        [token, showNotification, t],
    );

    const handleDeleteFile = useCallback(
        async (fileId: string) => {
            setIsDeleting(true);
            if (!token) {
                console.error("No token available");
                return;
            }
            if (!fileId) {
                console.error("No file id");
                return;
            }

            setIsRefreshing(true);
            try {
                await deleteVectorDocument({
                    token,
                    fileId,
                });

                await refreshFiles();
            } catch (error) {
                console.error("Failed to delete file:", error);
            } finally {
                setIsDeleting(false);
                setIsRefreshing(false);
            }
        },
        [token, refreshFiles],
    );

    const triggerQueueRefresh = useCallback(() => {
        setQueueRefreshTrigger((prev) => prev + 1);
    }, []);

    const value = useMemo(
        () => ({
            folders,
            selectedFolderId,
            files,
            isRefreshing,
            isDeleting,
            pagination,
            setFolders,
            setFiles,
            setSelectedFolderId,
            handleEditFolder,
            handleDeleteFolder,
            handleSelectFolder,
            addFolder,
            reorderFolders,
            refreshFolders,
            refreshFiles,
            handleDeleteFile,
            hasActiveVectorSearch,
            isFileLimitReached,
            triggerQueueRefresh,
            queueRefreshTrigger,
            updateQueryParams,
            searchParams,
        }),
        [
            folders,
            selectedFolderId,
            files,
            isRefreshing,
            isDeleting,
            pagination,
            setFolders,
            setFiles,
            setSelectedFolderId,
            handleEditFolder,
            handleDeleteFolder,
            handleSelectFolder,
            addFolder,
            reorderFolders,
            refreshFolders,
            refreshFiles,
            handleDeleteFile,
            hasActiveVectorSearch,
            isFileLimitReached,
            triggerQueueRefresh,
            queueRefreshTrigger,
            updateQueryParams,
            searchParams,
        ],
    );

    return <DatabaseContext.Provider value={value}>{children}</DatabaseContext.Provider>;
};

export const useDatabase = (): DatabaseContextType => {
    const context = useContext(DatabaseContext);
    if (context === undefined) {
        throw new Error("useDatabase must be used within a DatabaseProvider");
    }
    return context;
};
